import "core-js/modules/es6.promise";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "regenerator-runtime/runtime";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import MemoryQueueAsync from './queue/MemoryQueueAsync';
import createBatchTransportQueue from './BatchTransportQueue';
import deepMergeInto from './deepMergeInto';

var defaultOnError = function defaultOnError(err) {
  if (process.env.NODE_ENV === 'test') {
    // Throw when running tests so the test fails
    throw err;
  } else if (process.env.NODE_ENV !== 'production') {
    // Log information for the developer

    /* eslint-disable no-console */
    console.error('An error occurred while sending analytics events:');

    if (err.stack) {
      console.error(err.stack);
    } else {
      console.error(err);
    }
    /* eslint-enable no-console */

  } // Don't do anything in production

};
/**
 * Create a new instance of an analytics client instance.
 *
 * @module
 */

/**
 * Create a new analytics instance for tracking and transporting batches of events
 * @param {Object} options - Options used to create and configure analytics instance behavior
 * @default {}
 * @param {Function} options.transport
 *                   (async) Function to transport a batch of events to a remote endpoint,
 * @param {Number} [options.batchDelay=0]
 *                   Duration to wait (in ms) to batch events before making a transport call
 * @param {Object} [options.batchQueue]
 *                   An persistent storage queue that implements asynchronous interface:
 *                   `enqueue(item), dequeue(limit), toArray()`
 * @param {String} [options.eventBaseClass='base_event']
 *                   Name of the base eventType. decorators registered for this eventType will apply to ALL events
 * @param {Object} [options.eventPayloadDecorators=[]]
 *                   An array of payload "decorators" that add or modify values on event payloads based on event type.
 * @param {Object} [options.eventSubclasses={}]
 *                   Map of event subclasses.  { [childEventType]: parentEventType }.
 *                   childEventType will apply decorations for parenEventType, as well as its own decorators.
 *                   Subclassing is recursive, i.e. a -> b -> c.  c would apply decorators for a, b, & c
 * @param {Function} [options.onError]
 *                   Custom error handler (overrides defaultOnError)
 *                   Called with an error object when a transport or enqueue error occurs.
 * @param {Function} [options.onTransportResults]
 *                   Called with transported events and the results of transporting them.
 * @param {Object}  [options.state={}]
 *                   Analytics state.  By default, this is a local empty `{}` object.
 *                   Override this to share a state `object` between different instances of `analytics` from `createAnalytics`
 *
 * @returns {analytics} an analytics instance
 */


export default function createAnalytics() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$enabled = _ref.enabled,
      initialEnabled = _ref$enabled === void 0 ? true : _ref$enabled,
      transport = _ref.transport,
      _ref$batchDelay = _ref.batchDelay,
      batchDelay = _ref$batchDelay === void 0 ? 0 : _ref$batchDelay,
      _ref$batchQueue = _ref.batchQueue,
      batchQueue = _ref$batchQueue === void 0 ? new MemoryQueueAsync() : _ref$batchQueue,
      _ref$eventBaseClass = _ref.eventBaseClass,
      eventBaseClass = _ref$eventBaseClass === void 0 ? 'base_event' : _ref$eventBaseClass,
      _ref$eventPayloadDeco = _ref.eventPayloadDecorators,
      eventPayloadDecorators = _ref$eventPayloadDeco === void 0 ? [] : _ref$eventPayloadDeco,
      _ref$eventSubclasses = _ref.eventSubclasses,
      eventSubclasses = _ref$eventSubclasses === void 0 ? {} : _ref$eventSubclasses,
      _ref$onError = _ref.onError,
      onError = _ref$onError === void 0 ? defaultOnError : _ref$onError,
      _ref$onTransportResul = _ref.onTransportResults,
      onTransportResults = _ref$onTransportResul === void 0 ? function () {} : _ref$onTransportResul,
      _ref$state = _ref.state,
      state = _ref$state === void 0 ? {} : _ref$state;

  if (typeof transport !== 'function') throw new Error("'options.transport' method required to 'createAnalytics', got: ".concat(transport));

  var getState = function getState(key) {
    return state[key];
  };

  var setState = function setState(key, value) {
    state[key] = value; // eslint-disable-line no-param-reassign

    return getState(key);
  };
  /**
   * An array of track() promises since the last flush(). This allows calls
   * to flush() to make sure any track() calls that came before the flush()
   * call are finished before flushing. See usage in flush().
   */


  var trackPromises = [];
  /**
   * The initial enabled/disabled state can be set in the options passed to
   * createAnalytics. However, analytics can be later enabled or disabled
   * with the setEnabled function.
   */

  var enabled = initialEnabled;
  /**
   * Allows instrumentation to be enabled or disabled on the fly. When
   * disabled, calls to track() will not result in events being recorded.
   *
   * @param {boolean} isEnabled
   */

  function setEnabled(isEnabled) {
    enabled = isEnabled;
  } // apply all registered decorations to a given payload for a given eventType


  function decorateEventPayload(_x, _x2, _x3) {
    return _decorateEventPayload.apply(this, arguments);
  } // apply decorations to a given payload for an eventType


  function _decorateEventPayload() {
    _decorateEventPayload = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee4(eventType, payload, decoratorType) {
      var eventPayload, parentClass;
      return regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              eventPayload = payload; // first apply decorators for any matching eventType
              // of which this eventType is a subclass

              parentClass = eventSubclasses[eventType];

              if (!parentClass) {
                _context4.next = 6;
                break;
              }

              _context4.next = 5;
              return decorateEventPayload(parentClass, eventPayload, decoratorType);

            case 5:
              eventPayload = _context4.sent;

            case 6:
              return _context4.abrupt("return", eventPayloadDecorators.reduce(
              /*#__PURE__*/
              function () {
                var _ref4 = _asyncToGenerator(
                /*#__PURE__*/
                regeneratorRuntime.mark(function _callee3(promise, decorator) {
                  var decorateFn, oldPayload;
                  return regeneratorRuntime.wrap(function _callee3$(_context3) {
                    while (1) {
                      switch (_context3.prev = _context3.next) {
                        case 0:
                          if (!(decorator.eventType !== eventType)) {
                            _context3.next = 2;
                            break;
                          }

                          return _context3.abrupt("return", promise);

                        case 2:
                          decorateFn = decorator[decoratorType];

                          if (decorateFn) {
                            _context3.next = 5;
                            break;
                          }

                          return _context3.abrupt("return", promise);

                        case 5:
                          _context3.next = 7;
                          return promise;

                        case 7:
                          oldPayload = _context3.sent;
                          _context3.t0 = deepMergeInto;
                          _context3.t1 = oldPayload;
                          _context3.next = 12;
                          return decorateFn(oldPayload);

                        case 12:
                          _context3.t2 = _context3.sent;
                          return _context3.abrupt("return", (0, _context3.t0)(_context3.t1, _context3.t2));

                        case 14:
                        case "end":
                          return _context3.stop();
                      }
                    }
                  }, _callee3);
                }));

                return function (_x11, _x12) {
                  return _ref4.apply(this, arguments);
                };
              }(), Promise.resolve(eventPayload)));

            case 7:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));
    return _decorateEventPayload.apply(this, arguments);
  }

  function applyDecorations(_x4, _x5, _x6) {
    return _applyDecorations.apply(this, arguments);
  } // create the batch transport queue for track to push events into


  function _applyDecorations() {
    _applyDecorations = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee5(eventType, payload, decoratorType) {
      return regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              _context5.t0 = decorateEventPayload;
              _context5.t1 = eventType;
              _context5.next = 4;
              return decorateEventPayload(eventBaseClass, payload, decoratorType);

            case 4:
              _context5.t2 = _context5.sent;
              _context5.t3 = decoratorType;
              return _context5.abrupt("return", (0, _context5.t0)(_context5.t1, _context5.t2, _context5.t3));

            case 7:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5);
    }));
    return _applyDecorations.apply(this, arguments);
  }

  var batchTransport = createBatchTransportQueue({
    delay: batchDelay,
    queue: batchQueue,
    transport: transport,
    onError: onError,
    onTransportResults: onTransportResults,
    // Decorate each event with any payload decorators registered to run at transport time
    beforeTransport: function () {
      var _beforeTransport = _asyncToGenerator(
      /*#__PURE__*/
      regeneratorRuntime.mark(function _callee2(events) {
        return regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                return _context2.abrupt("return", Promise.all(events.map(
                /*#__PURE__*/
                function () {
                  var _ref3 = _asyncToGenerator(
                  /*#__PURE__*/
                  regeneratorRuntime.mark(function _callee(_ref2) {
                    var eventType, payload;
                    return regeneratorRuntime.wrap(function _callee$(_context) {
                      while (1) {
                        switch (_context.prev = _context.next) {
                          case 0:
                            eventType = _ref2.eventType, payload = _ref2.payload;
                            _context.t0 = eventType;
                            _context.next = 4;
                            return applyDecorations(eventType, payload, 'onTransport');

                          case 4:
                            _context.t1 = _context.sent;
                            return _context.abrupt("return", {
                              eventType: _context.t0,
                              payload: _context.t1
                            });

                          case 6:
                          case "end":
                            return _context.stop();
                        }
                      }
                    }, _callee);
                  }));

                  return function (_x8) {
                    return _ref3.apply(this, arguments);
                  };
                }())));

              case 1:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      function beforeTransport(_x7) {
        return _beforeTransport.apply(this, arguments);
      }

      return beforeTransport;
    }()
  });

  function doTrack(_x9, _x10) {
    return _doTrack.apply(this, arguments);
  }

  function _doTrack() {
    _doTrack = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee6(eventType, payload) {
      var clonedPayload, event;
      return regeneratorRuntime.wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              // Create a clone of the payload that we'll apply decorators to and mutate
              // so that the input `payload` argument object is not mutated.
              clonedPayload = deepMergeInto({}, payload);
              _context6.next = 3;
              return applyDecorations(eventType, clonedPayload, 'onCapture');

            case 3:
              // Allow values passed in the payload to override "onCapture" decorators.
              deepMergeInto(clonedPayload, payload);
              event = {
                eventType: eventType,
                payload: clonedPayload
              };
              _context6.next = 7;
              return batchTransport.enqueue(event);

            case 7:
              // Flush events after batch delay, but do not await the promise because
              // track() doesn't wait until events are sent before resolving.
              batchTransport.flush({
                immediate: false
              });

            case 8:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6);
    }));
    return _doTrack.apply(this, arguments);
  }

  function flush() {
    return _flush.apply(this, arguments);
  }

  function _flush() {
    _flush = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee7() {
      var _ref5,
          _ref5$immediate,
          immediate,
          promises,
          _args7 = arguments;

      return regeneratorRuntime.wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              _ref5 = _args7.length > 0 && _args7[0] !== undefined ? _args7[0] : {}, _ref5$immediate = _ref5.immediate, immediate = _ref5$immediate === void 0 ? true : _ref5$immediate;
              // Wait for any outstanding enqueue() calls to complete. This allows code
              // like this:
              //
              //     analytics.track(...)
              //     await analytics.flush()
              //
              // To work as expected even though there was no await on the track() call.
              promises = trackPromises;
              trackPromises = [];
              _context7.next = 5;
              return Promise.all(promises);

            case 5:
              _context7.next = 7;
              return batchTransport.flush({
                immediate: immediate
              });

            case 7:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7);
    }));
    return _flush.apply(this, arguments);
  }

  function track() {
    return _track.apply(this, arguments);
  }
  /**
   * @typedef analytics
   * @type {Object}
   * @property {Function} track - `track(eventName, payload)`
   */

  /** @type analytics */


  function _track() {
    _track = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee8() {
      var _len,
          args,
          _key,
          _console,
          promise,
          _args8 = arguments;

      return regeneratorRuntime.wrap(function _callee8$(_context8) {
        while (1) {
          switch (_context8.prev = _context8.next) {
            case 0:
              for (_len = _args8.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = _args8[_key];
              }

              if (enabled) {
                _context8.next = 4;
                break;
              }

              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                (_console = console).log.apply(_console, ['nwjs-analytics event disabled'].concat(args));
              }

              return _context8.abrupt("return", undefined);

            case 4:
              promise = doTrack.apply(void 0, args);
              trackPromises.push(promise);
              return _context8.abrupt("return", promise);

            case 7:
            case "end":
              return _context8.stop();
          }
        }
      }, _callee8);
    }));
    return _track.apply(this, arguments);
  }

  return {
    flush: flush,
    track: track,
    setState: setState,
    getState: getState,
    setEnabled: setEnabled
  };
}